import React from 'react';
import { Modal, Box, Typography, Form } from '@lucid/lego';
import { createSendDocusignFormFields } from './formFields';
import styles from './Docusign.module.scss';

/**
 * Form data for the Docusign modal
 */
export type SendDocusignFormData = {
  firstName: string;
  lastName: string;
  email: string;
  placeOnHold: boolean;
};

/**
 * Props for the Docusign component
 */
export type DocusignProps = {
  /** Test ID for the component */
  dataTestId: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when form is submitted */
  onSend: (data: SendDocusignFormData) => void;
  /** Theme variant */
  theme?: 'dark' | 'light';
  /** Initial values for the form */
  initialValues?: Partial<SendDocusignFormData>;
};

/**
 * Docusign modal component for sending Docusign to customer
 * @component
 * @param {DocusignProps} props - The component props
 * @returns {JSX.Element} The Docusign modal
 * @example
 * <Docusign
 *   dataTestId="docusign-modal"
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   onSend={handleSend}
 *   theme="dark"
 * />
 */
const Docusign: React.FC<DocusignProps> = ({
  dataTestId,
  isOpen,
  onClose,
  onSend,
  theme = 'dark',
  initialValues = {},
}) => {
  const formFields = createSendDocusignFormFields(initialValues, { onCancel: onClose });

  /**
   * Handles successful form submission
   * @param data - Form data from the submission
   */
  const handleSuccess = (data: unknown) => {
    const formData = data as SendDocusignFormData;
    onSend({
      firstName: formData.firstName?.trim() || '',
      lastName: formData.lastName?.trim() || '',
      email: formData.email?.trim() || '',
      placeOnHold: formData.placeOnHold || false,
    });
  };

  /**
   * Handles form validation errors
   */
  const handleError = () => {
    // Validation errors are handled by the Form component
  };

  return (
    <Modal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title="Send Docusign"
      theme={theme}
      size="medium"
      className={styles.modal}
    >
      <Box className={styles.content}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          Please confirm the information below to send Docusign to customer and proceed with order.
        </Typography>

        <Box className={styles.formFields} mt={24}>
          <Form
            id="send-docusign-form"
            theme={theme}
            formFields={formFields}
            onSuccess={handleSuccess}
            onError={handleError}
            isFluid
          />
        </Box>
      </Box>
    </Modal>
  );
};

Docusign.displayName = 'Docusign';

export default Docusign;
